import { useMemo, useState } from 'react';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { AssetFmvOutlookResponse } from '../dto/fmv-outlook.js';
import { SX_PALETTE } from './charts.js';
import { formatUsd } from './format.js';

/**
 * "FMV outlook if held" chart + card (Asset Detail Visualizations design doc,
 * mock 2c). Renders in both portals: solid FMV history line, dashed forecast
 * median from today, shaded cone, dotted today divider, Refresh Cliff marker,
 * and a muted book-depreciation reference line.
 *
 * The cone's meaning differs by caller (client = fixed ±15%, ops = real
 * low/high bands) — pass the matching `footnote` so the label never
 * overclaims.
 */

// Theme-token colors with hardcoded fallbacks — same convention as FmvLine.tsx
// so the chart renders in the ops portal (defines the CSS vars) and the
// customer portal (doesn't).
const BORDER = 'hsl(var(--border, 240 5% 91%))';
const MUTED = 'hsl(var(--muted-foreground, 215 20% 65%))';
const TOOLTIP_STYLE = {
  fontSize: 12,
  borderRadius: 6,
  borderColor: BORDER,
  backgroundColor: 'hsl(var(--popover, 0 0% 100%))',
  color: 'hsl(var(--popover-foreground, 240 10% 4%))',
} as const;

const PRIMARY = SX_PALETTE[0];
const BOOK = SX_PALETTE[4]; // amber — visually distinct from the FMV violet
const CLIFF = 'hsl(var(--destructive, 0 72% 51%))';

export interface FmvOutlookHistoryPoint {
  pricedAt: string;
  fmvMedUsd: number;
}

interface OutlookRow {
  month: string;
  histUsd?: number;
  fcUsd?: number;
  cone?: [number, number];
  bookUsd?: number;
}

const monthOf = (iso: string): string => iso.slice(0, 7);

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** '2028-07' → 'Jul 2028' (header stat). */
export const monthLong = (m: string): string => {
  const mm = Number(m.slice(5, 7));
  return `${MONTH_NAMES[mm - 1] ?? m} ${m.slice(0, 4)}`;
};

/** Serial month index for ±1-month tolerant lookups. */
const monthIndex = (m: string): number =>
  Number(m.slice(0, 4)) * 12 + Number(m.slice(5, 7));

/** Merge history / forecast / book series into one month-keyed table. */
function buildSeries(
  history: FmvOutlookHistoryPoint[],
  outlook: AssetFmvOutlookResponse,
): { rows: OutlookRow[]; todayMonth: string | null } {
  const byMonth = new Map<string, OutlookRow>();
  const row = (month: string): OutlookRow => {
    let r = byMonth.get(month);
    if (!r) {
      r = { month };
      byMonth.set(month, r);
    }
    return r;
  };

  // History: last value per month wins (snapshots are intra-month noise here).
  for (const h of [...history].sort(
    (a, b) => new Date(a.pricedAt).getTime() - new Date(b.pricedAt).getTime(),
  )) {
    row(monthOf(h.pricedAt)).histUsd = h.fmvMedUsd;
  }

  const newestHistory = [...history].sort(
    (a, b) => new Date(b.pricedAt).getTime() - new Date(a.pricedAt).getTime(),
  )[0];
  const todayMonth =
    outlook.forecast[0]?.month ??
    (newestHistory ? monthOf(newestHistory.pricedAt) : null);

  for (const p of outlook.forecast) {
    const r = row(p.month);
    r.fcUsd = p.medUsd;
    r.cone = [p.loUsd, p.hiUsd];
  }
  // Connect solid → dashed: the forecast's first point doubles as history.
  const fc0 = outlook.forecast[0];
  if (fc0) {
    const first = row(fc0.month);
    if (first.histUsd === undefined) first.histUsd = fc0.medUsd;
  }

  // Axis spans from the launch/addition date: seed the origin row so the
  // chart starts there even when history and book data begin later.
  if (outlook.windowStartMonth) row(outlook.windowStartMonth);

  // Full book line from its anchor — the axis is launch-anchored by design.
  if (outlook.bookLine) {
    for (const b of outlook.bookLine) {
      row(b.month).bookUsd = b.valueUsd;
    }
  }

  const rows = [...byMonth.values()].sort((a, b) => a.month.localeCompare(b.month));
  return { rows, todayMonth };
}

export function FmvOutlookChart({
  history,
  outlook,
  height = 240,
  seriesColor = PRIMARY,
  bookColor = BOOK,
  cliffColor = CLIFF,
}: {
  history: FmvOutlookHistoryPoint[];
  outlook: AssetFmvOutlookResponse;
  height?: number;
  /** FMV actual/forecast line + uncertainty cone color. Defaults to the
   * shared palette's primary (violet) — the ops portal's brand hue. The
   * customer portal reserves violet as semantic-only (new-condition-band),
   * so it overrides this to the emerald ramp (SIL-335) rather than this
   * component picking a portal-specific default. */
  seriesColor?: string;
  /** Book-depreciation reference line color. Defaults to the shared
   * palette's amber — overridden by callers that reserve amber for a
   * cliff/warning marker instead (SIL-335). */
  bookColor?: string;
  /** Refresh-cliff marker line + label color. */
  cliffColor?: string;
}) {
  const { rows, todayMonth } = useMemo(
    () => buildSeries(history, outlook),
    [history, outlook],
  );

  // Category axis: snap the cliff month to the nearest chart month at or
  // before it so the marker renders even off the quarterly grid.
  const cliffX = useMemo(() => {
    if (!outlook.refreshCliff) return null;
    const m = outlook.refreshCliff.month;
    const atOrBefore = rows.filter((r) => r.month <= m);
    return atOrBefore.length > 0 ? atOrBefore[atOrBefore.length - 1]!.month : null;
  }, [rows, outlook.refreshCliff]);

  if (rows.length === 0) {
    return (
      <div
        className="flex items-center justify-center text-xs"
        style={{ height, color: MUTED }}
      >
        No pricing history yet.
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={rows} margin={{ top: 8, right: 12, bottom: 0, left: 4 }}>
        <CartesianGrid stroke={BORDER} strokeDasharray="3 3" vertical={false} />
        <XAxis
          dataKey="month"
          tick={{ fontSize: 10.5, fill: MUTED }}
          tickLine={false}
          axisLine={{ stroke: BORDER }}
          minTickGap={24}
          tickFormatter={monthLong}
        />
        <YAxis
          tick={{ fontSize: 10.5, fill: MUTED }}
          tickLine={false}
          axisLine={false}
          width={52}
          domain={['auto', 'auto']}
          tickFormatter={(v: number) => formatUsd(v, { compact: true })}
        />
        <Tooltip
          contentStyle={TOOLTIP_STYLE}
          formatter={(value: number | [number, number], name: string) =>
            Array.isArray(value)
              ? [`${formatUsd(value[0])} – ${formatUsd(value[1])}`, name]
              : [formatUsd(value), name]
          }
        />
        <Area
          dataKey="cone"
          name="Uncertainty"
          fill={seriesColor}
          fillOpacity={0.1}
          stroke="none"
          connectNulls
          legendType="none"
        />
        <Line
          dataKey="histUsd"
          name="FMV (actual)"
          stroke={seriesColor}
          strokeWidth={2}
          dot={false}
          connectNulls
        />
        <Line
          dataKey="fcUsd"
          name="FMV (forecast)"
          stroke={seriesColor}
          strokeWidth={2}
          strokeDasharray="5 4"
          dot={false}
          connectNulls
        />
        <Line
          dataKey="bookUsd"
          name="Book value"
          stroke={bookColor}
          strokeWidth={1.5}
          strokeDasharray="2 2"
          dot={false}
          connectNulls
        />
        {todayMonth && (
          <ReferenceLine x={todayMonth} stroke={MUTED} strokeDasharray="3 3" />
        )}
        {cliffX && (
          <ReferenceLine
            x={cliffX}
            stroke={cliffColor}
            strokeDasharray="3 3"
            label={{
              value: `Price cliff ${monthLong(outlook.refreshCliff!.month)}`,
              position: 'insideTopRight',
              fontSize: 10,
              fill: cliffColor,
            }}
          />
        )}
      </ComposedChart>
    </ResponsiveContainer>
  );
}

/**
 * Book cell for a forecast row. Book points step quarterly from the anchor
 * date while forecast points step quarterly from today, so the two grids can
 * sit one month apart — accept the nearest book point within a month.
 */
function bookCell(bookByMonth: Map<string, number>, month: string): string {
  const exact = bookByMonth.get(month);
  if (exact !== undefined) return formatUsd(exact);
  const target = monthIndex(month);
  for (const [m, v] of bookByMonth) {
    if (Math.abs(monthIndex(m) - target) <= 1) return formatUsd(v);
  }
  return '-';
}

/**
 * Full card: header label + "held to <month>" stat, chart, footnote, and a
 * "Forecast · N rows" table toggle (Month / Median / Low / High / Book).
 */
export function FmvOutlookCard({
  history,
  outlook,
  footnote,
  className = '',
}: {
  history: FmvOutlookHistoryPoint[];
  outlook: AssetFmvOutlookResponse;
  footnote: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);

  const bookByMonth = useMemo(() => {
    const m = new Map<string, number>();
    for (const b of outlook.bookLine ?? []) m.set(b.month, b.valueUsd);
    return m;
  }, [outlook.bookLine]);

  const delta = outlook.heldDeltaUsd;
  const lastMonth = outlook.forecast[outlook.forecast.length - 1]?.month;
  const stat =
    delta !== null && lastMonth
      ? `held to ${monthLong(lastMonth)}: ${delta < 0 ? '−' : '+'}${formatUsd(Math.abs(delta))} median`
      : null;

  return (
    <div className={`rounded-xl border border-border bg-card p-6 ${className}`}>
      <div className="flex items-baseline justify-between gap-4">
        <div className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
          FMV outlook if held
          {/* SIL-1828 (R2): server flag from the outlook DTO. */}
          {outlook.isDirectionalBaseline && (
            <span
              data-testid="outlook-directional-label"
              className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold normal-case tracking-normal text-amber-800"
            >
              Directional
            </span>
          )}
        </div>
        {stat && (
          <div className="font-mono text-[11px] text-muted-foreground">{stat}</div>
        )}
      </div>
      <div className="mt-3.5">
        <FmvOutlookChart history={history} outlook={outlook} />
      </div>
      <div className="mt-2.5 text-[11px] text-muted-foreground">{footnote}</div>
      {outlook.forecast.length > 0 && (
        <>
          <div className="mt-3 flex items-center gap-4 border-t border-border pt-2.5">
            <button
              type="button"
              onClick={() => setOpen((o) => !o)}
              className="text-[11px] font-semibold text-brand-700 hover:text-brand-800"
            >
              {open ? 'Hide data' : `Forecast · ${outlook.forecast.length} rows`}
            </button>
          </div>
          {open && (
            <div className="mt-2.5 overflow-hidden rounded-md border border-border">
              <div
                className="grid bg-muted px-3 py-1.5 text-[9.5px] uppercase tracking-widest text-muted-foreground"
                style={{ gridTemplateColumns: 'repeat(5, minmax(0, 1fr))' }}
              >
                <span>Month</span>
                <span>Median</span>
                <span>Low</span>
                <span>High</span>
                <span>Book</span>
              </div>
              {outlook.forecast.map((p) => (
                <div
                  key={p.month}
                  className="grid border-t border-border px-3 py-1.5 font-mono text-[11px] text-foreground"
                  style={{ gridTemplateColumns: 'repeat(5, minmax(0, 1fr))' }}
                >
                  <span>{monthLong(p.month)}</span>
                  <span>{formatUsd(p.medUsd)}</span>
                  <span>{formatUsd(p.loUsd)}</span>
                  <span>{formatUsd(p.hiUsd)}</span>
                  <span>{bookCell(bookByMonth, p.month)}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
